import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { ComponentStatus } from '../types';
import { COMPONENT_LABEL_KEYS, COMPONENT_ORDER } from '../utils';
import {
  ProgressSection, ProgressBar, ProgressFill, ProgressText,
  LoadingShell, LoadingHeadline, LoadingPulse,
  LoadingGrid, LoadingTile, LoadingTileTop, LoadingTileLeft,
  LoadingTileIcon, LoadingTileName, LoadingTileDuration,
  LoadingTileMicro, LoadingMiniSpinner,
  LoadingFindings, LoadingFindingsLabel, LoadingFindingsRow, LoadingBullet,
} from '../styles';

interface Props {
  progress: number;
  components: Record<string, ComponentStatus>;
  fullResult: Record<string, any> | null;
  startedAt: number | null;
  statusMsg?: string;
}

const ICON: Record<string, string> = {
  pending: '○',
  running: '◎',
  completed: '✓',
  failed: '✗',
  skipped: '—',
};

const RUNNING_KEY: Record<string, string> = {
  techniques: 'analysis.loading.running.techniques',
  ai_tampered: 'analysis.loading.running.ai_tampered',
  claims: 'analysis.loading.running.claims',
  source_assessment: 'analysis.loading.running.source_assessment',
};

const PENDING_KEY: Record<string, string> = {
  techniques: 'analysis.loading.pending.techniques',
  ai_tampered: 'analysis.loading.pending.ai_tampered',
  claims: 'analysis.loading.pending.claims',
  source_assessment: 'analysis.loading.pending.source_assessment',
};

function fmtMs(ms: number) {
  if (ms < 1000) return `${ms}ms`;
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

/** Build a component-specific micro-stat text from its result (when completed). */
function buildDoneMicro(name: string, result: any): string | null {
  if (!result) return null;
  switch (name) {
    case 'techniques': {
      const count = result.techniques_detected?.length || 0;
      if (count === 0) return 'no techniques detected';
      const score = typeof result.manipulation_score === 'number'
        ? result.manipulation_score
        : parseFloat(result.manipulation_score) || 0;
      return `${count} detected · score ${score.toFixed(0)}`;
    }
    case 'ai_tampered': {
      const prob = typeof result.ai_probability === 'number'
        ? result.ai_probability
        : parseFloat(result.ai_probability) || 0;
      return `${prob.toFixed(0)}% AI probability`;
    }
    case 'claims': {
      const total = result.total_claims || 0;
      const f = result.verified_false || 0;
      const t = result.verified_true || 0;
      if (total === 0) return 'no claims found';
      return `${total} claims · ${t} true · ${f} false`;
    }
    case 'source_assessment': {
      const trust = result.trust_score;
      if (trust == null) return 'no source data';
      return `trust ${trust}%`;
    }
    default:
      return null;
  }
}

/** Build the running list of "Detected so far" bullets from progressive results. */
function buildLiveFindings(result: Record<string, any> | null, t: TFunction): { color: string; text: string }[] {
  if (!result) return [];
  const out: { color: string; text: string }[] = [];

  if (result.techniques?.techniques_detected?.length) {
    const n = result.techniques.techniques_detected.length;
    out.push({
      color: '#ef4444',
      text: t('analysis.loading.live.techniques', { count: n }),
    });
  }
  if (result.ai_tampered?.ai_probability != null) {
    const p = result.ai_tampered.ai_probability;
    out.push({
      color: p >= 60 ? '#f97316' : '#22c55e',
      text: t('analysis.loading.live.aiProbability', { prob: p.toFixed(0) }),
    });
  }
  if (result.claims?.total_claims != null) {
    const total = result.claims.total_claims;
    const f = result.claims.verified_false || 0;
    if (total > 0) {
      const falseSuffix = f > 0
        ? t('analysis.loading.live.falseSuffix', { count: f })
        : '';
      out.push({
        color: f > 0 ? '#ef4444' : '#3b82f6',
        text: t('analysis.loading.live.claimsVerified', { count: total, falseSuffix }),
      });
    }
  }
  if (result.source_assessment?.trust_score != null) {
    const trust = result.source_assessment.trust_score;
    out.push({
      color: trust >= 70 ? '#22c55e' : trust >= 40 ? '#f97316' : '#ef4444',
      text: t('analysis.loading.live.sourceTrust', { trust }),
    });
  }
  return out;
}

export const LoadingState: React.FC<Props> = ({ progress, components, fullResult, startedAt, statusMsg }) => {
  const { t } = useTranslation();
  const [now, setNow] = useState<number>(() => Date.now());

  useEffect(() => {
    if (!startedAt) return;
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, [startedAt]);

  const elapsed = startedAt ? now - startedAt : 0;
  const findings = buildLiveFindings(fullResult, t);

  return (
    <>
      <ProgressSection>
        <ProgressBar><ProgressFill width={progress} /></ProgressBar>
        <ProgressText>
          <span>{statusMsg || t('analysis.loading.analyzing')}</span>
          <span>{Math.round(progress)}% · {fmtMs(elapsed)}</span>
        </ProgressText>
      </ProgressSection>

      <LoadingShell>
        <LoadingHeadline>
          <LoadingPulse />
          {t('analysis.loading.analyzingInput')}
        </LoadingHeadline>

        <LoadingGrid>
          {COMPONENT_ORDER.map(name => {
            const comp = components[name];
            const status = comp?.status || 'pending';
            const result = fullResult?.[name];
            const doneMicro = status === 'completed' ? buildDoneMicro(name, result) : null;
            const micro = status === 'completed'
              ? (doneMicro || t('analysis.loading.microDone'))
              : status === 'running'
                ? (RUNNING_KEY[name] ? t(RUNNING_KEY[name]) : t('analysis.loading.microWorking'))
                : status === 'failed'
                  ? t('analysis.loading.microFailed')
                  : status === 'skipped'
                    ? t('analysis.loading.microSkipped')
                    : (PENDING_KEY[name] ? t(PENDING_KEY[name]) : t('analysis.loading.microQueued'));

            return (
              <LoadingTile key={name} status={status}>
                <LoadingTileTop>
                  <LoadingTileLeft>
                    <LoadingTileIcon status={status}>{ICON[status]}</LoadingTileIcon>
                    <LoadingTileName>{t(COMPONENT_LABEL_KEYS[name] || name)}</LoadingTileName>
                  </LoadingTileLeft>
                  {comp?.duration_ms != null && status === 'completed' && (
                    <LoadingTileDuration>{(comp.duration_ms / 1000).toFixed(1)}s</LoadingTileDuration>
                  )}
                </LoadingTileTop>
                <LoadingTileMicro status={status}>
                  {status === 'running' && <LoadingMiniSpinner />}
                  {micro}
                </LoadingTileMicro>
              </LoadingTile>
            );
          })}
        </LoadingGrid>

        {findings.length > 0 && (
          <LoadingFindings>
            <LoadingFindingsLabel>{t('analysis.loading.detectedSoFar')}</LoadingFindingsLabel>
            {findings.map((f, i) => (
              <LoadingFindingsRow key={i}>
                <LoadingBullet color={f.color} />
                <span>{f.text}</span>
              </LoadingFindingsRow>
            ))}
          </LoadingFindings>
        )}
      </LoadingShell>
    </>
  );
};
